import React from "react";
import type { Bucket, MetadataKey, TruncationCause } from "../types";
import type { GroupedLineFacet, HeatmapData } from "../utils/chartData";
import type { DrillClick } from "../utils/drilldown";
import { GroupBySelector } from "./GroupBySelector";
import { GroupedTimeSeriesChart } from "./GroupedTimeSeriesChart";
import { HeatmapChart } from "./HeatmapChart";
import { TruncationWarning } from "./TruncationWarning";
// Direct import (not the ../../../ui barrel) so jest doesn't transitively pull
// in import.meta-using modules; kept consistent with the chart components.
import { EmptyState } from "../../../ui/EmptyState";
import { GROUP_LIMIT_OPTIONS } from "../constants";

interface GroupBySectionProps {
  metadataKeys: MetadataKey[];
  groupByKey?: string;
  onGroupByKeyChange: (key?: string) => void;
  /** How many tag values to render — the top-N the grouped fetch asked for. */
  groupLimit: number;
  onGroupLimitChange: (limit: number) => void;
  facets: GroupedLineFacet[];
  heatmapData: HeatmapData;
  /** criteria → display label (evaluator name) for facet titles / heatmap rows. */
  criteriaLabels: Record<string, string>;
  bucket: Bucket;
  groupColors: Record<string, string>;
  loading: boolean;
  truncationCauses: TruncationCause[];
  /**
   * Ungrouped per-criterion data (single "" group) rendered as the scorecard
   * when no group-by key is selected, so the heatmap card is never blank.
   */
  scorecardData: HeatmapData;
  scorecardLabels: Record<string, string>;
  scorecardLoading: boolean;
  /** When provided, clicking a group line / heatmap cell drills into Traces. */
  onDrill?: (click: DrillClick) => void;
}

/**
 * The "Group by" section beneath the ungrouped charts: a tag-key selector and,
 * once a key is chosen, a heatmap (criteria × tag value) stacked above the
 * grouped time-series. Both draw from the same grouped fetch; a single shared
 * empty state covers the no-data case.
 *
 * The "Show top N" control sets how many tag values that fetch asks for, so a
 * customer with more values than the default can see them instead of losing
 * them to a silent cap (#1414).
 */
export function GroupBySection({
  metadataKeys,
  groupByKey,
  onGroupByKeyChange,
  groupLimit,
  onGroupLimitChange,
  facets,
  heatmapData,
  criteriaLabels,
  bucket,
  groupColors,
  loading,
  truncationCauses,
  scorecardData,
  scorecardLabels,
  scorecardLoading,
  onDrill,
}: GroupBySectionProps) {
  const isEmpty = !loading && heatmapData.criteria.length === 0;
  return (
    <div className="space-y-4" data-testid="group-by-section">
      <div className="bg-card rounded-lg shadow-sm border border-border p-5">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="font-semibold text-base text-foreground">Group by</h3>
          <GroupBySelector
            metadataKeys={metadataKeys}
            groupByKey={groupByKey}
            onChange={onGroupByKeyChange}
          />
          {/* Only meaningful once there are values to limit. Ranks by volume,
              so "top 25" is the 25 busiest values — say so, don't imply "all". */}
          {groupByKey && (
            <div
              className="inline-flex items-center gap-2 text-sm text-muted-foreground"
              data-testid="group-limit-selector"
            >
              <span>Show top</span>
              <div
                className="inline-flex items-center rounded-full border border-border bg-muted/40 p-0.5"
                role="group"
                aria-label="Number of values to show"
              >
                {GROUP_LIMIT_OPTIONS.map((limit) => {
                  const active = limit === groupLimit;
                  return (
                    <button
                      key={limit}
                      type="button"
                      onClick={() => onGroupLimitChange(limit)}
                      aria-pressed={active}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        active
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {limit}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        {!groupByKey && (
          <p className="text-sm text-muted-foreground mt-3">
            Select a tag to break the scores down by its values (e.g. by agent or
            customer).
          </p>
        )}
      </div>

      {/* No group-by selected → per-criterion scorecard (bars) so the card is
          never blank; picking a tag expands it into the 2-D heatmap below. */}
      {!groupByKey && (
        <HeatmapChart
          data={scorecardData}
          groupByKey=""
          labels={scorecardLabels}
          loading={scorecardLoading}
          onDrill={onDrill}
        />
      )}

      {groupByKey && (
        <>
          {truncationCauses.length > 0 && (
            <TruncationWarning
              causes={truncationCauses}
              groupByKey={groupByKey}
            />
          )}
          {isEmpty ? (
            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <EmptyState
                message="No data for this grouping"
                description={`No evaluations are tagged with “${groupByKey}” for the current filters.`}
                variant="info"
              />
            </div>
          ) : (
            <>
              <HeatmapChart
                data={heatmapData}
                groupByKey={groupByKey}
                labels={criteriaLabels}
                loading={loading}
                onDrill={onDrill}
              />
              <GroupedTimeSeriesChart
                facets={facets}
                bucket={bucket}
                groupColors={groupColors}
                groupByKey={groupByKey}
                labels={criteriaLabels}
                loading={loading}
                onDrill={onDrill}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}
